<template>
  <div v-if="currApp" class="rf-category-pg-wrapper">
    <div class="all-segments-content-div" :class="{ 'hide-subnav-menu': !subnavOpenStatus }">
      <RfSettingList :subnavOpenStatus="subnavOpenStatus" @showHideSubnav="showHideSubnav" />
      <div class="all-segments-list-rightcol">
        <div class="flex h-16 max-h-16 items-center justify-between gap-4 bg-white px-8">
          <span class="my-auto flex items-center">
            <span class="text-heading-1 text-beige-90">
              {{ SettingTitles[currSetting]?.title || SettingTitles[currSetting] || "Settings" }}
            </span>
            <a
              v-if="SettingTitles[currSetting]?.docs"
              target="_blank"
              title="View docs"
              class="ml-2 inline-flex"
              :href="SettingTitles[currSetting].docs"
            >
              <RfHelpDocsIcon class="fill-beige-50! my-auto h-6! w-6!" />
            </a>
          </span>
          <RfButtonNew
            v-if="SettingTitles[currSetting]?.action"
            size="large"
            class="min-h-10!"
            :text="SettingTitles[currSetting].action"
            :disabled="additionalPropsForAction[currSetting]?.disabled"
            @click="$refs[currSetting].click()"
          />
        </div>
        <div class="rf-detail-content-wrapper flex flex-col-reverse">
          <div v-if="showSaveButton" class="rf-settings-content rf-settings-content--submit">
            <div
              class="rf-retention-form-btn-div border-beige-10 shadow-card flex! rounded-b-lg border border-t-0 border-solid bg-white pt-8 pb-10"
            >
              <RfButtonNew
                size="x-large"
                class="mr-10! ml-auto! px-10!"
                :disabled="userStore.isDisabledWithMessage || saveButtonDisabled"
                @click="onSettingsUpdateClick"
              >
                <span class="inline-flex items-center justify-center gap-4 py-1.5 pr-2">
                  <RfSaveIcon class="h-6! w-6!" />
                  <span>Save changes</span>
                </span>
              </RfButtonNew>
            </div>
          </div>
          <RfSystem
            v-if="currSetting === 'system'"
            :key="renderKey"
            ref="system"
            :model="currApp"
            @update:formValid="systemFormValid = $event"
          />
          <RfSetupUserId
            v-if="currSetting === 'user_id'"
            :key="renderKey"
            ref="user_id"
            :app="currApp"
          />
          <RfSetupUsageTracking
            v-if="currSetting === 'usage_tracking'"
            :key="renderKey"
            ref="usage_tracking"
            :app="currApp"
          />
          <RfSetupCustomTraits
            v-if="currSetting === 'segment_traits'"
            :key="renderKey"
            ref="segment_traits"
            :app="currApp"
          />
          <RfSettingsTags v-if="currSetting === 'tags'" :key="renderKey" ref="tags" />
          <RfSimpleCard v-if="currSetting === 'triggers'" class="p-10">
            <div class="settings-actions-tabs mx-0! mt-0! mb-5">
              <div :class="{ selectedTab: triggerTab === 'triggers' }">
                <button @click="triggerTab = 'triggers'">Triggers</button>
              </div>
              <div :class="{ selectedTab: triggerTab === 'custom_triggers' }">
                <button @click="triggerTab = 'custom_triggers'">Advanced Triggers</button>
              </div>
            </div>
            <RfTriggerSettings
              v-if="triggerTab === 'triggers'"
              :key="renderKey"
              :currApp="currApp"
            />
            <RfClientActions
              v-if="triggerTab === 'custom_triggers'"
              :key="renderKey"
              ref="custom_triggers"
              :app="currApp"
              actionType="custom_trigger"
            />
          </RfSimpleCard>
          <RfDataSourceSettings v-if="currSetting === 'data_sources'" ref="data_sources" />
          <RfSimpleCard
            v-if="isConnector"
            class="p-10"
            @vue:mounted="appsStore.getAppConnectors(currApp.id)"
          >
            <div class="settings-actions-tabs mx-0! mt-0! mb-5">
              <div :class="{ selectedTab: actionTab === 'connectors' }">
                <button @click="actionTab = 'connectors'">External</button>
              </div>
              <div :class="{ selectedTab: actionTab === 'custom_connectors' }">
                <button @click="actionTab = 'custom_connectors'">API Actions</button>
              </div>
              <div :class="{ selectedTab: actionTab === 'client_actions' }">
                <button @click="actionTab = 'client_actions'">Website Actions</button>
              </div>
              <div v-if="ethocaProvisioned(currApp)" :class="{ selectedTab: actionTab === 'banking' }">
                <button @click="actionTab = 'banking'">Banking</button>
              </div>
            </div>
            <RfConnectors
              v-if="actionTab === 'connectors'"
              :key="renderKey"
              ref="connectors"
              :app="currApp"
              @onSettingsUpdateClick="onSettingsUpdateClick"
            />
            <RfCustomConnectors
              v-if="actionTab === 'custom_connectors'"
              :key="renderKey"
              ref="custom_connectors"
              :app="currApp"
            />
            <RfClientActions
              v-if="actionTab === 'client_actions'"
              :key="renderKey"
              ref="client_actions"
              :app="currApp"
              actionType="website_action"
            />
            <RfBanking
              v-if="actionTab === 'banking'"
              :key="renderKey"
              ref="banking"
              :app="currApp"
              @onSettingsUpdateClick="onSettingsUpdateClick"
            />
          </RfSimpleCard>
          <RfPlacementZones v-if="currSetting === 'zones'" ref="zones" />
          <RfCustomDevices v-if="currSetting === 'custom_devices'" ref="custom_devices" />
          <RfManageUsers
            v-if="currSetting === 'users'"
            :key="renderKey"
            v-model:tab="userTab"
            :currApp="currApp"
            :currentCompany="currApp?.company_id ? { id: currApp.company_id } : null"
          />
          <RfAppMessages v-if="currSetting === 'app_messages'" ref="appMessages" :app="currApp" />
          <RfCustomWebSnippet
            v-if="currSetting === 'custom_web_snippet' && userStore.isAdmin"
            :key="renderKey"
            ref="custom_web_snippet"
            :app="currApp"
          />
          <RfCssStyles
            v-if="currSetting === 'css_styles' && userStore.isCurrentSuperAdmin"
            ref="css_styles"
          />
          <RfDownloadSettings
            v-if="currSetting === 'download_csv'"
            :key="renderKey"
            :app="currApp"
          />
          <RfAppAdmin
            v-if="currSetting === 'admin' && userStore.isCurrentSuperAdmin"
            :key="renderKey"
            ref="admin"
            :model="currApp"
            @update:renderKey="renderKey++"
          />
          <RfSalesAdmin
            v-if="
              currSetting === 'sales_admin' && (userStore.isSales || userStore.isCurrentSuperAdmin)
            "
            :key="renderKey"
            ref="sales_admin"
            :model="currApp"
          />
          <RfAwsMarketplaceSettings
            v-if="currSetting === 'aws_marketplace'"
            ref="awsMarketplaceSettings"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from "lodash-es";
import RfSettingList from "@/components/RfSettings/RfSettingList.vue";
import RfSystem from "@/components/RfSettings/RfSystem.vue";
import RfSetupCustomTraits from "@/components/RfSettings/RfSetupCustomTraits.vue";
import RfSetupUserId from "@/components/RfSettings/RfSetupUserId.vue";
import RfSetupUsageTracking from "@/components/RfSettings/RfSetupUsageTracking.vue";
import RfManageUsers from "@/components/RfSettings/RfManageUsers.vue";
import RfAppAdmin from "@/components/RfSettings/RfAdmin.vue";
import RfSalesAdmin from "@/components/RfSettings/RfSalesAdmin.vue";
import RfConnectors from "@/components/RfSettings/RfConnectors.vue";
import RfCustomConnectors from "@/components/RfSettings/RfCustomConnectors.vue";
import RfCustomWebSnippet from "@/components/RfSettings/RfCustomWebSnippet.vue";
import RfCssStyles from "@/components/RfSettings/RfCssStyles.vue";
import RfClientActions from "@/components/RfSettings/RfClientActions.vue";
import RfBanking from "@/components/RfSettings/RfBanking.vue";
import RfPlacementZones from "@/components/RfSettings/RfPlacementZones.vue";
import RfTriggerSettings from "@/components/RfSettings/RfTriggerSettings.vue";
import RfDownloadSettings from "@/components/RfSettings/RfDownloadSettings.vue";
import RfSimpleCard from "@/components/RfSimpleCard.vue";
import * as Sentry from "@sentry/vue";
import SettingTitles from "@/utils/constants/SettingTitlesConstants";
import { ethocaProvisioned } from "@/utils/ethoca";
import RfDataSourceSettings from "@/components/RfSettings/RfDataSourceSettings.vue";
import RfCustomDevices from "@/components/RfSettings/RfCustomDevices.vue";
import RfAwsMarketplaceSettings from "@/views/RfAwsMarketplaceSettings.vue";
import { useToastsStore } from "@/pinia/toastsStore";
import RfSettingsTags from "@/components/RfSettings/RfSettingsTags/RfSettingsTags.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import { useLegacyLastErrorStore } from "@/pinia/legacyLastErrorStore";
import { useUserSettings } from "@/utils/composables/useUserSettings";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import { ref } from "vue";
import RfSaveIcon from "@/components/icons/RfSaveIcon.vue";
import RfHelpDocsIcon from "@/components/icons/RfHelpDocsIcon.vue";
import RfAppMessages from "@/components/RfSettings/RfAppMessages.vue";
import { useDynamicTitle } from "@/utils/composables/useDynamicTitle";
import { useTemplateRef } from "vue";

export default {
  name: "RfSettings",
  components: {
    RfSettingList,
    RfSystem,
    RfManageUsers,
    RfSetupCustomTraits,
    RfAppAdmin,
    RfSalesAdmin,
    RfConnectors,
    RfSetupUserId,
    RfSetupUsageTracking,
    RfCustomConnectors,
    RfCustomWebSnippet,
    RfCssStyles,
    RfClientActions,
    RfBanking,
    RfPlacementZones,
    RfTriggerSettings,
    RfDownloadSettings,
    RfSimpleCard,
    RfCustomDevices,
    RfDataSourceSettings,
    RfAwsMarketplaceSettings,
    RfSettingsTags,
    RfButtonNew,
    RfSaveIcon,
    RfHelpDocsIcon,
    RfAppMessages,
  },
  setup: () => {
    const subnavOpenStatus = ref(true);
    const { getUserSetting, setUserSetting } = useUserSettings();
    return {
      toastsStore: useToastsStore(),
      SettingTitles,
      subnavOpenStatus,
      appsStore: useAppsStore(),
      userStore: useUserStore(),
      legacyLastErrorStore: useLegacyLastErrorStore(),
      getUserSetting,
      setUserSetting,
      appMessages: useTemplateRef("appMessages"),
      ethocaProvisioned,
    };
  },
  data() {
    return {
      updating: false,
      renderKey: 0,
      systemFormValid: true,
      actionsTabs: null,
      actionTab: "connectors",
      userTab: "tab5",
      triggerTab: "triggers",
    };
  },
  computed: {
    saveButtonDisabled() {
      if (
        this.currSetting === "app_messages" &&
        this.appMessages?.selectedTab === "notifications"
      ) {
        return this.appMessages?.disabledAppNotifications;
      }
      if (this.currSetting === "system") return !this.systemFormValid;
      return false;
    },
    lastError() {
      return this.legacyLastErrorStore.lastError;
    },
    currApp() {
      return this.appsStore.app;
    },
    currSetting() {
      return this.$route.params.stab || "system";
    },
    isConnector() {
      return this.currSetting.match(/connectors\b/);
    },
    showSaveButton() {
      const hideButtonSettings = [
        "usage_tracking",
        "segment_traits",
        "tags",
        "triggers",
        "data_sources",
        "zones",
        "custom_devices",
        "sales_admin",
        "download_csv",
      ];
      if (hideButtonSettings.includes(this.currSetting)) return false;
      if (this.isConnector && this.actionTab !== "custom_connectors") return false;
      if (this.currSetting === "app_messages") {
        const tab = this.appMessages?.selectedTab ?? "messages";
        if (tab === "messages") return false;
      }
      if (
        this.currSetting === "users" &&
        ["tab2", "tab3", "tab4", "tab5", "tab6"].includes(this.userTab)
      )
        return false;

      return true;
    },
    additionalPropsForAction() {
      return {
        data_sources: { disabled: this.userStore.isDisabledWithMessage },
        custom_devices: { disabled: this.userStore.isDisabledWithMessage },
        zones: { disabled: this.userStore.isDisabledWithMessage },
      };
    },
  },
  watch: {
    currApp(to) {
      if (to == null) {
        this.$router.back();
      } else if (this.updating) {
        this.toastsStore.create({ type: "success", body: "Settings updated successfully" });
        this.updating = false;
      }
    },
    lastError(to) {
      if (to) {
        this.toastsStore.create({ type: "error", body: to.message });
      }
    },
  },
  mounted() {
    useDynamicTitle(() => {
      const title = SettingTitles[this.currSetting]?.title || SettingTitles[this.currSetting];
      return title && `${title} | Settings`;
    });

    if (typeof this.getUserSetting("subnavOpenStatusStorage") !== "undefined") {
      this.subnavOpenStatus = this.getUserSetting("subnavOpenStatusStorage");
    }
  },
  methods: {
    showHideSubnav() {
      const status = !this.subnavOpenStatus;
      this.subnavOpenStatus = status;
      this.setUserSetting("subnavOpenStatusStorage", status);
    },
    async onSettingsUpdateClick() {
      try {
        if (this.currSetting === "app_messages") {
          const tab = this.appMessages?.selectedTab ?? "messages";
          if (tab === "notifications") return this.appMessages?.submitAppNotifications();
          return false;
        }
        if (this.currSetting === "aws_marketplace")
          return this.$refs.awsMarketplaceSettings.updateSettings();

        const settingsRef = this.$refs[this.currSetting];
        if (
          typeof settingsRef?.validateForm === "function" &&
          !(await settingsRef.validateForm())
        ) {
          return false;
        }

        const newApp = cloneDeep(this.currApp);
        let fillModel;
        if (this.isConnector) {
          fillModel = this.$refs[this.actionTab].fillModel;
        } else if (this.currSetting === "triggers" && this.triggerTab === "custom_triggers") {
          fillModel = this.$refs[this.triggerTab].fillModel;
        } else if (this.$refs[this.currSetting]) {
          fillModel = this.$refs[this.currSetting].fillModel;
        }
        if (fillModel) {
          fillModel(newApp);
        }

        this.updating = true;
        const includeProviders =
          this.isConnector ||
          this.currSetting === "custom_connectors" ||
          this.currSetting === "user_id";
        await this.appsStore.updateApp({ appObj: newApp, includeProviders });
        this.renderKey++;
      } catch (error) {
        this.toastsStore.create({ type: "error", body: `${error}. Please try again.` });
        Sentry.captureException(error);
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.rf-settings-content--submit ~ div:not(.rf-setting-card) > :last-child,
.rf-settings-content--submit ~ .rf-setting-card {
  border-bottom-left-radius: 0px !important;
  border-bottom-right-radius: 0px !important;
  padding-bottom: 0px !important;
  border-bottom: none;
}
.rf-detail-content-wrapper {
  margin-top: 10px;
  max-width: 1600px;
  padding-left: 32px;
  padding-right: 32px;
}

:deep(.rf-settings-content) {
  .v-card.rf-retention-form {
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
  }

  .v-card.rf-retention-form-btn-div {
    padding-bottom: 40px !important;
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
  }
}
</style>
